import ResponsiveRow from '../shared/ResponsiveRow';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import '../financials/financialsResponsive.css';
import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';
import useModuleAccess from '../../hooks/useModuleAccess';

// ─── constants ───────────────────────────────────────────────────────────────

const FREQUENCIES = ['weekly', 'bi-weekly', 'monthly', 'quarterly', 'annually'];
const STATUSES = ['active', 'paused', 'cancelled'];

const EMPTY_FORM = {
    account_id: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    start_date: '',
    end_date: '',
    next_billing_date: '',
    status: 'active',
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0);
}

function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusBadge(status) {
    const map = {
        active: 'bg-green-100 text-green-800',
        paused: 'bg-yellow-100 text-yellow-800',
        cancelled: 'bg-red-100 text-red-800',
    };
    const cls = map[status] ?? 'bg-gray-100 text-gray-800';
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        </span>
    );
}

// ─── component ───────────────────────────────────────────────────────────────

function AutoBillingView({ theme, isUser = false }) {
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEdit = !isUser || financialEditAllowed;
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // form state
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // ── data loading ──────────────────────────────────────────────────────────

    const loadSchedules = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        const { data, error } = await supabaseClient
            .from('recurring_billing')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) {
            console.error('[AutoBillingView] load error:', error);
            setLoadError(error.message);
        } else {
            setSchedules(data ?? []);
        }
        setLoading(false);
    }, []);

    useEffect(() => { loadSchedules(); }, [loadSchedules]);

    // ── form helpers ──────────────────────────────────────────────────────────

    const openCreate = () => {
        if (!canEdit) return;
        setForm(EMPTY_FORM);
        setFormMode('create');
        setEditing(null);
        setSaveError(null);
        setShowForm(true);
    };

    const openEdit = (schedule) => {
        if (!canEdit) return;
        setForm({
            account_id: schedule.account_id ?? '',
            description: schedule.description ?? '',
            amount: schedule.amount ?? '',
            frequency: schedule.frequency ?? 'monthly',
            start_date: schedule.start_date ?? '',
            end_date: schedule.end_date ?? '',
            next_billing_date: schedule.next_billing_date ?? '',
            status: schedule.status ?? 'active',
        });
        setFormMode('edit');
        setEditing(schedule);
        setSaveError(null);
        setShowForm(true);
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditing(null);
        setSaveError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // ── save / delete ─────────────────────────────────────────────────────────

    const handleSave = async (e) => {
        e.preventDefault();
        if (!canEdit) return;
        setSaving(true);
        setSaveError(null);

        const parsedAmount = parseFloat(form.amount);
        if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            setSaveError('Amount must be a number greater than zero.');
            setSaving(false);
            return;
        }

        const payload = {
            account_id: form.account_id || null,
            description: form.description || null,
            amount: parsedAmount,
            frequency: form.frequency,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
            next_billing_date: form.next_billing_date || null,
            status: form.status,
        };

        let error;
        if (formMode === 'create') {
            ({ error } = await supabaseClient.from('recurring_billing').insert([payload]));
        } else {
            ({ error } = await supabaseClient
                .from('recurring_billing')
                .update(payload)
                .eq('id', editing.id));
        }

        if (error) {
            console.error('[AutoBillingView] save error:', error);
            setSaveError(error.message);
        } else {
            setShowForm(false);
            setEditing(null);
            await loadSchedules();
        }
        setSaving(false);
    };

    const handlePause = async (schedule) => {
        if (!canEdit) return;
        const next = schedule.status === 'paused' ? 'active' : 'paused';
        const { error } = await supabaseClient
            .from('recurring_billing')
            .update({ status: next })
            .eq('id', schedule.id);
        if (error) {
            console.error('[AutoBillingView] pause error:', error);
        } else {
            await loadSchedules();
        }
    };

    const handleDelete = async (schedule) => {
        if (!canEdit) return;
        if (!window.confirm(`Delete recurring billing schedule "${schedule.description || schedule.id}"?`)) return;
        const { error } = await supabaseClient
            .from('recurring_billing')
            .delete()
            .eq('id', schedule.id);
        if (error) {
            console.error('[AutoBillingView] delete error:', error);
        } else {
            await loadSchedules();
        }
    };

    // ── render ────────────────────────────────────────────────────────────────

    const dark = theme === 'dark';
    const bg = dark ? 'bg-gray-800' : 'bg-white';
    const textPrimary = 'text-[var(--ink)]';
    const textMuted = dark ? 'text-gray-400' : 'text-gray-600';
    const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${
        dark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
    }`;

    return (
        <div className={`ia-financials rounded-lg shadow-md p-6 ${bg}`}>
            {/* Header */}
            <ResponsiveRow className="ia-financial-header flex items-center justify-between mb-6">
                <div>
                    <h3 className={`text-2xl font-bold mb-1 ${textPrimary}`}>Auto-Billing</h3>
                    <p className={textMuted}>Manage recurring billing schedules</p>
                </div>
                {canEdit && <button
                    onClick={openCreate}
                    className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] text-sm font-semibold rounded-lg hover:bg-[var(--btn-fill-hover)] transition-colors"
                >
                    + New Schedule
                </button>}
            </ResponsiveRow>

            {/* Create / Edit form */}
            {canEdit && showForm && (
                <div className={`mb-6 p-4 rounded-lg border ${dark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <h4 className={`text-lg font-semibold mb-4 ${textPrimary}`}>
                        {formMode === 'create' ? 'New Recurring Schedule' : 'Edit Schedule'}
                    </h4>
                    <form onSubmit={handleSave}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Account ID</label>
                                <input
                                    name="account_id"
                                    value={form.account_id}
                                    onChange={handleChange}
                                    placeholder="Account UUID"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Description</label>
                                <input
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Monthly retainer…"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Amount (USD) <span className="text-red-500">*</span></label>
                                <input
                                    name="amount"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    required
                                    value={form.amount}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Frequency <span className="text-red-500">*</span></label>
                                <select name="frequency" value={form.frequency} onChange={handleChange} className={inputCls} required>
                                    {FREQUENCIES.map(f => (
                                        <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Start Date</label>
                                <input name="start_date" type="date" value={form.start_date} onChange={handleChange} className={inputCls} />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>End Date</label>
                                <input name="end_date" type="date" value={form.end_date} onChange={handleChange} className={inputCls} />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Next Billing Date</label>
                                <input name="next_billing_date" type="date" value={form.next_billing_date} onChange={handleChange} className={inputCls} />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${textMuted}`}>Status</label>
                                <select name="status" value={form.status} onChange={handleChange} className={inputCls}>
                                    {STATUSES.map(s => (
                                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {saveError && (
                            <p className="text-red-500 text-sm mb-3">{saveError}</p>
                        )}

                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] text-sm font-semibold rounded-lg hover:bg-[var(--btn-fill-hover)] disabled:opacity-50 transition-colors"
                            >
                                {saving ? 'Saving…' : formMode === 'create' ? 'Create Schedule' : 'Save Changes'}
                            </button>
                            <button
                                type="button"
                                onClick={cancelForm}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                                    dark ? 'border-gray-500 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* State feedback */}
            {loading && (
                <div className={`text-center py-12 ${textMuted}`}>Loading schedules…</div>
            )}
            {!loading && loadError && (
                <div className="text-center py-12">
                    <p className="text-red-500 font-semibold">Failed to load schedules</p>
                    <p className="text-red-400 text-sm mt-1">{loadError}</p>
                    <button onClick={loadSchedules} className="mt-4 px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] text-sm rounded-lg hover:bg-[var(--btn-fill-hover)]">
                        Retry
                    </button>
                </div>
            )}

            {/* Empty state */}
            {!loading && !loadError && schedules.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔄</div>
                    <p className={`text-lg font-semibold ${textPrimary}`}>No recurring schedules yet</p>
                    <p className={textMuted}>Create a schedule to start billing clients automatically.</p>
                </div>
            )}

            {/* Table */}
            {!loading && !loadError && schedules.length > 0 && (
                <div className="overflow-x-auto">
                    <ResponsiveTable className="w-full">
                        <thead className={dark ? 'bg-gray-700' : 'bg-gray-50'}>
                            <tr>
                                {['Description', 'Amount', 'Frequency', 'Next Billing', 'Status', ...(canEdit ? ['Actions'] : [])].map(col => (
                                    <th key={col} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {schedules.map(schedule => (
                                <tr key={schedule.id} className={`transition-colors ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                    <ResponsiveCell label="Description" className={`px-4 py-3 text-sm font-medium ${textPrimary}`}>
                                        {schedule.description || <span className={textMuted}>—</span>}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Amount" className={`ia-financial-amount px-4 py-3 text-sm font-semibold ${textPrimary}`}>
                                        {formatCurrency(schedule.amount)}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Frequency" className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {schedule.frequency
                                            ? schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)
                                            : '—'}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Next Billing" className={`px-4 py-3 text-sm ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {formatDate(schedule.next_billing_date)}
                                    </ResponsiveCell>
                                    {canEdit && <ResponsiveCell label="Status" className="px-4 py-3 text-sm">
                                        {statusBadge(schedule.status)}
                                    </ResponsiveCell>}
                                    <ResponsiveCell label="Actions" className="px-4 py-3 text-sm">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openEdit(schedule)}
                                                className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handlePause(schedule)}
                                                className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
                                                    schedule.status === 'paused'
                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                                }`}
                                            >
                                                {schedule.status === 'paused' ? 'Resume' : 'Pause'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(schedule)}
                                                className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </ResponsiveCell>
                                </tr>
                            ))}
                        </tbody>
                    </ResponsiveTable>
                </div>
            )}
        </div>
    );
}

export default AutoBillingView;
